import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { AnimatePresence, motion } from 'framer-motion';
import { Redirect, useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Button, ButtonText } from '@saucelabs/ds-button';
import { Box, Col, Row, HStack, VStack } from '@saucelabs/sl-component-grid';
import { ContentLoader, Loader } from '@saucelabs/sl-component-loader';
import { records as userRecords } from '@saucelabs/sl-module-user';
import { Typography } from '@saucelabs/ds-typography';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';

import {
  CONCURRENCY_KEY,
  CONFIG_STORAGE_KEY,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_PURCHASE,
  THREEDS_AUTH_ERROR_TYPE,
  THREEDS_FAILURE_MESSAGE,
  PLAN_INTERVAL,
  ALLOWED_CREDIT_BLOCK_VALUES,
} from '../../constants';
import CouponCode from '../CouponCode';
import GridContainer from '../Layout/GridContainer';
import PaymentForm from '../PaymentForm';
import Subscription from '../../records/Subscription';
import TestFairyTermsModal from '../TestFairyTermsModal';
import { OrderSummary } from '../OrderSummary';
import { PlanConfiguration } from '../../records/PlanConfiguration';
import { PlanTile } from '../SummaryTile';
import { SectionTitle } from '../Layout/SectionTitle';
import { getProductType, Plan } from '../../records/Plan';
import { handle3DSecure } from '../../utils/threeDSecure';
import { utils as planUtils } from '../../ducks/plans';
import BaseStyles from '../Layout/base';
import { PRODUCT_AREA, PRODUCT_SUB_AREAS, SUBJECT } from '../../tracking';

const crumbList = [
  { name: 'Subscription and Billing', url: `/${ROUTE_PARENT_SECTION}` },
  { name: 'Review and Pay' },
];

const OldCheckout = ({
  activeSubscriptions,
  allPlans,
  applyCoupon,
  checkoutError,
  checkoutPreview,
  checkoutPurchase,
  clearNotifications,
  clearPurchaseData,
  currentMonthlyRate,
  currentSubscriptions,
  fetchCheckoutPreview,
  handleCheckoutSuccess,
  initialize,
  initialized,
  isPreviewLoaded,
  orderItems,
  resetPreview,
  setPurchase,
  totalAvailableCredits,
  user,
}) => {
  let cleanup3DSecureListeners;
  const [hasPreview, setHasPreview] = React.useState(false);
  const paymentVerificationElementRef = React.createRef();
  const isUserFree = user.isFreeUser();
  const location = useLocation();
  const { interval, blocks, product, projects } = parse(location.search);
  const shouldLoadCreditPlan = (interval === PLAN_INTERVAL.MONTH ||
    interval === PLAN_INTERVAL.YEAR) && ALLOWED_CREDIT_BLOCK_VALUES[interval].includes(+blocks);
  const hasMobileQualityInCart = orderItems.get('mobile-quality')?.[CONCURRENCY_KEY] === 1;
  const shouldRedirectToPurchase = !localStorage.getItem(CONFIG_STORAGE_KEY) &&
    !shouldLoadCreditPlan;

  React.useEffect(() => {
    if (!initialized) {
      initialize();
    }

    if (location.state?.purchase) {
      setPurchase(location.state.purchase);
    }

    if (shouldLoadCreditPlan) {
      setPurchase({
        [product || 'usage']: { interval, creditBlocks: +blocks, projects: +projects },
      });
    }

    if (cleanup3DSecureListeners) {
      cleanup3DSecureListeners();
    }

    return () => resetPreview();
  }, []);
  const [isPurchasing, setIsPurchasing] = React.useState(false);
  const [paymentFormState, setPaymentFormState] = React.useState({});
  const onError = (message) => {
    trackStandardEvent(
      STANDARD_EVENTS.SERVER_RESPONDED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CHECKOUT,
      SUBJECT.ORDER_FAILED,
      { message },
    );
    setIsPurchasing(false);
    checkoutError(message);
  };

  const doPurchase = ({ threeDsToken = null }) => {
    clearNotifications();
    setIsPurchasing(true);
    checkoutPurchase({
      threeDsTokenId: threeDsToken?.id,
    })
      .then(({ orderItems: checkoutOrderItems, paymentInfo }) => {
        trackStandardEvent(
          STANDARD_EVENTS.SERVER_RESPONDED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CHECKOUT,
          SUBJECT.ORDER_SUCCESS,
        );
        clearPurchaseData();
        return handleCheckoutSuccess({
          activeSubscriptions,
          isUserFree,
          orderItems: checkoutOrderItems,
          paymentInfo,
        });
      })
      .catch((error) => {
        const { response } = error;
        if (response?.data?.detail?.three_d_secure_action_token_id) {
          const { remove3DListeners } = handle3DSecure({
            onError: (innerError) => {
              const message = innerError.code === THREEDS_AUTH_ERROR_TYPE ?
                THREEDS_FAILURE_MESSAGE :
                `Error processing payment verification: ${innerError.message}`;

              onError(message);
            },
            onVerified: doPurchase,
            response,
            verificationElement: paymentVerificationElementRef.current,
          });
          cleanup3DSecureListeners = remove3DListeners;
        } else {
          let { message } = error;
          // show error detail from recurly otherwise show error code
          if (response?.data && response.status === 422) {
            const { detail } = response.data;
            message = detail || message;
            if (Array.isArray(detail)) {
              // validation error due to user waiting for too long. Retry the whole operation.
              // We ignore the current token's value. This operation will generate a new one.
              if (detail[0]?.param === 'transaction.amount') {
                fetchCheckoutPreview().then(doPurchase);
                return;
              }
              onError(detail[0].message);
            } else {
              onError(message);
            }
          } else {
            onError('Purchase request failed, please try again');
          }
        }
      });
  };

  const onPaymentInfoSet = async () => {
    await fetchCheckoutPreview();
    setHasPreview(true);
  };

  const subscriptionPreviews = React.useMemo(
    () => Object.values(planUtils.orderItemsToSubscriptionPreviews(orderItems, allPlans)),
    [allPlans, orderItems]);

  if (shouldRedirectToPurchase) {
    return <Redirect to="/billing/purchase" />;
  }

  return (
    <>
      <Breadcrumbs list={crumbList} />
      <GridContainer>
        <main>
          <Row>
            <Col
              lg={12}
              tagName="article"
            >
              <VStack gap={3}>
                <Typography
                  className="my-0"
                  disableSpacing
                  variant="heading-1"
                >
                  Review and Pay
                </Typography>
                <Box>
                  <VStack
                    gap={4}
                    justifyContent="space-between"
                  >
                    <ContentLoader show={!initialized}>
                      <section className="px-5 mt-4">
                        <SectionTitle headerLevel={3}>Order Overview</SectionTitle>
                        <VStack gap={2}>
                          {subscriptionPreviews
                            .map((preview) => (
                              <PlanTile
                                key={preview.plan.id}
                                currentCredits={totalAvailableCredits}
                                currentSubscription={activeSubscriptions.find((sub) => (
                                  getProductType(sub.plan) === getProductType(preview.plan)
                                ))}
                                editPlanLink={location.state?.purchase}
                                subscriptionPreview={preview}
                              />
                            ))}
                        </VStack>
                      </section>
                    </ContentLoader>
                    <section className="px-5">
                      <PaymentForm
                        isPurchase
                        onFormStateChange={setPaymentFormState}
                        onPaymentInfoSet={onPaymentInfoSet}
                      />
                    </section>
                    <ContentLoader show={!initialized}>
                      <section className="px-5 mt-5">
                        <SectionTitle headerLevel={3}>New Charges Breakdown</SectionTitle>
                        <Row between="xs">
                          <Col
                            md={6}
                            xs
                          >
                            <AnimatePresence initial>
                              {(hasPreview || isPreviewLoaded) && (
                                <motion.div
                                  animate={{ opacity: 1, y: 0 }}
                                  initial={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2, ease: 'easeOut' }}
                                >
                                  <CouponCode
                                    applyCoupon={applyCoupon}
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>
                            <OrderSummary
                              allPlans={allPlans}
                              couponSavings={checkoutPreview.discount}
                              currentMonthlyRate={currentMonthlyRate}
                              currentSubscriptions={currentSubscriptions}
                              isPurchase
                              orderItems={orderItems}
                              taxAmount={checkoutPreview.tax}
                              totalDueToday={checkoutPreview.balance}
                            />
                          </Col>
                        </Row>
                      </section>
                      <section className="px-5 mt-5 mb-4">
                        {hasMobileQualityInCart && (
                          <Row between="xs">
                            <Col
                              sm={6}
                              xs={12}
                            >
                              <TestFairyTermsModal />
                            </Col>
                          </Row>
                        )}
                        <Row between="xs">
                          <Col xs>
                            <HStack gap={2}>
                              <Button
                                color="primary"
                                disabled={(
                                  !isPreviewLoaded ||
                                  isPurchasing ||
                                  paymentFormState.invalid
                                )}
                                onPress={doPurchase}
                                variant="contained"
                              >
                                <Loader
                                  className={BaseStyles['loader-disabled']}
                                  fontSize={1.4}
                                  show={isPurchasing}
                                  text="Validating..."
                                >
                                  Pay Now
                                </Loader>
                              </Button>
                              <ButtonText
                                disabled={isPurchasing}
                                to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
                              >
                                Back
                              </ButtonText>
                            </HStack>
                          </Col>
                        </Row>
                      </section>
                    </ContentLoader>
                  </VStack>
                </Box>
              </VStack>
            </Col>
            <div ref={paymentVerificationElementRef} />
          </Row>
        </main>
      </GridContainer>
    </>
  );
};

OldCheckout.propTypes = {
  activeSubscriptions: ImmutablePropTypes.listOf(Subscription).isRequired,
  allPlans: PropTypes.arrayOf(PropTypes.instanceOf(Plan)).isRequired,
  applyCoupon: PropTypes.func.isRequired,
  checkoutError: PropTypes.func.isRequired,
  checkoutPreview: PropTypes.shape({
    balance: PropTypes.number,
    discount: PropTypes.number,
    subtotal: PropTypes.number,
    tax: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  checkoutPurchase: PropTypes.func.isRequired,
  clearNotifications: PropTypes.func.isRequired,
  clearPurchaseData: PropTypes.func.isRequired,
  currentMonthlyRate: PropTypes.number,
  currentSubscriptions: ImmutablePropTypes.listOf(Subscription).isRequired,
  fetchCheckoutPreview: PropTypes.func.isRequired,
  handleCheckoutSuccess: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  initialized: PropTypes.bool.isRequired,
  isPreviewLoaded: PropTypes.bool.isRequired,
  orderItems: ImmutablePropTypes.mapOf(PlanConfiguration).isRequired,
  resetPreview: PropTypes.func.isRequired,
  setPurchase: PropTypes.func.isRequired,
  totalAvailableCredits: PropTypes.number,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
};

OldCheckout.defaultProps = {
  currentMonthlyRate: null,
  totalAvailableCredits: null,
};

export { OldCheckout };
